import { Fragment, useMemo } from 'react';
import { Check, X } from 'lucide-react';

import { Survey } from 'types/survey';
import { Status, SurveyResponse } from 'types/surveyResponse';
import { TranslationsKeys, useTranslate } from 'hooks/useTranslate';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { InProgress } from 'components/ui/CustomIcons/InProgress';
import { classNameList } from 'utils/property';
import { getSubsection, isVisible } from 'utils/survey';
import styles from './SurveySectionList.module.scss';

interface IProps {
  surveyDefinition: Survey;
  surveyResponse: SurveyResponse;
  onSelect: (sectionId: string, subsectionId: string) => void;
}

export const SurveySectionList = ({ surveyDefinition, surveyResponse, onSelect }: IProps): JSX.Element => {
  const { t } = useTranslate();
  const ts = useSurveyTranslation(surveyDefinition);
  
  const counts = useMemo(() => {
    const result = { inProgress: 0, done: 0, skipped: 0 };
    for (const section of surveyDefinition.sections) {
      for (const surveyDefSubsection of section.subsections) {
        if (isVisible(surveyDefSubsection, surveyResponse)) {
          const surveyRespSubsection = getSubsection(surveyResponse, section.sectionId, surveyDefSubsection.subsectionId);
          switch (surveyRespSubsection?.status) {
            case Status.InProgress:
              result.inProgress++;
              break;
            case Status.Complete:
              result.done++;
              break;
            case Status.Skipped:
              result.skipped++;
              break;
          }
        }
      }
    }
    return result;
  }, [surveyResponse]);

  return (
    <div className={styles.surveySectionList}>
      {surveyDefinition.sections.map((s, index) => {
        if (!isVisible(s, surveyResponse)) {
          return null;
        }
        const responseSection = surveyResponse.sections.find((rs) => rs.sectionId === s.sectionId);

        return (
          <Fragment key={s.sectionId}>
            {index === 0 && <div className={styles.surveySectionListCounts}>
              <div>
                <div className={classNameList(styles.surveySectionListIcon, styles.success)}>
                  <Check />
                </div>
                {t(TranslationsKeys.common_surveyStatus_completed)}: {counts.done}
              </div>
              <div>
                <div className={classNameList(styles.surveySectionListIcon, styles.warning)}>
                  <InProgress />
                </div>
                {t(TranslationsKeys.common_surveyStatus_inProgress)}: {counts.inProgress}
              </div>
              <div>
                <div className={classNameList(styles.surveySectionListIcon, styles.skipped)}>
                  <X />
                </div>
                {t(TranslationsKeys.common_surveyStatus_skipped)}: {counts.skipped}
              </div>
            </div>}

            <div className={styles.surveySectionListSection}>{ts(s.name)}</div>
            
            <div className={styles.surveySectionListSubsections}>
              {s.subsections.map((ss) => {
                if (!isVisible(ss, surveyResponse)) {
                  return null;
                }
  
                const responseSubSection = responseSection
                  ? responseSection.subsections.find((rss) => rss.subsectionId === ss.subsectionId)
                  : undefined;
                const isSkipped = responseSubSection && responseSubSection.status === Status.Skipped;
  
                return (
                  <button
                    type="button"
                    key={ss.subsectionId}
                    className={classNameList(styles.surveySectionListSubsection, isSkipped && styles.skippedText)}
                    onClick={() => onSelect(s.sectionId, ss.subsectionId)}>
                    {ts(ss.name)}
                    {isSkipped && (
                      <div className={classNameList(styles.surveySectionListIcon, styles.skipped)}>
                        <X />
                      </div>
                    )}
                    {responseSubSection?.status === Status.InProgress && (
                      <div className={classNameList(styles.surveySectionListIcon, styles.warning)}>
                        <InProgress />
                      </div>
                    )}
                    {responseSubSection?.status === Status.Complete && (
                      <div className={classNameList(styles.surveySectionListIcon, styles.success)}>
                        <Check />
                      </div>
                    )}
                    {/* show whitespace "icon" if no icon necessary, to keep the same size buttons */}
                    {(responseSubSection?.status === Status.NotStarted || !responseSubSection) && 
                      <div className={classNameList(styles.surveySectionListIcon, styles.placeholder)} />}
                  </button>
                );
              })}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};
